/**
 * Rabies Vaccination Certificate (US) — PDF component and field definitions.
 *
 * Auto-filled: clinic header (Snoots Clifton, US), owner details, patient
 * details (age, weight, microchip), and — via the form — the date
 * vaccinated (defaults to today) and next vaccination due (date vaccinated +
 * validity).
 * Manual fields: product name, manufacturer, serial (lot) number, expiry date,
 * 1-year / 3-year validity, and vet name + license (via the vet sign-off block).
 *
 * Date fields are stored as ISO (YYYY-MM-DD) strings and rendered in US long
 * form (e.g. "July 20, 2026") via `formatCertDate`.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { PatientDetails } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import { VetSignoff } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

/** 1-year vs 3-year rabies vaccine validity, as indicated on US rabies certificates. */
export type RabiesVaccineDuration = '1-year' | '3-year'

export interface RabiesVaccinationCertificateFields {
  productName: string
  manufacturer: string
  serialNumber: string
  dateVaccinated: string
  expiryDate: string
  nextDueDate: string
  duration: RabiesVaccineDuration
  vetName: string
  vetLicenseNumber: string
}

export const defaultRabiesVaccinationCertificateFields: RabiesVaccinationCertificateFields = {
  productName: '',
  manufacturer: '',
  serialNumber: '',
  dateVaccinated: '',
  expiryDate: '',
  nextDueDate: '',
  // 3-year is the common rabies vaccine interval and matches the reference certificate.
  duration: '3-year',
  vetName: '',
  vetLicenseNumber: '',
}

/** Human-readable label for a rabies vaccine validity duration. */
export function formatRabiesDuration(duration: RabiesVaccineDuration): string {
  return duration === '1-year' ? '1 year' : '3 years'
}

/**
 * Formats an ISO date string (YYYY-MM-DD) as a US long date for the
 * certificate, e.g. "2026-07-20" → "July 20, 2026". Returns '' for empty or
 * unparseable input so the field renders as a blank fill-in line.
 */
export function formatCertDate(iso: string): string {
  if (!iso) return ''
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function RabiesVaccinationCertificatePdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: RabiesVaccinationCertificateFields
}) {
  const { patient, owner, weight, clinic } = context
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} showName />

        <Text style={styles.title}>Rabies Vaccination Certificate</Text>

        <OwnerDetails owner={owner} />

        <View style={styles.sectionGap}>
          <PatientDetails patient={patient} weight={weight} showAge showWeight showMicrochip />
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            This is to certify that {patient.name} has received a rabies vaccination as detailed
            below.
          </Text>
        </View>

        {/* Rabies vaccine details */}
        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold, textDecoration: 'underline' }}>
            Rabies Vaccination:
          </Text>
          <Spacer height={mm(2)} />
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.productName || undefined} />}>
              <Text>• Product name:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.manufacturer || undefined} />}>
              <Text>• Manufacturer:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse response={<FillField value={fields.serialNumber || undefined} />}>
              <Text>• Serial (lot) number:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse
              response={<FillField value={formatCertDate(fields.dateVaccinated) || undefined} />}
            >
              <Text>• Date vaccinated:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse
              response={<FillField value={formatCertDate(fields.expiryDate) || undefined} />}
            >
              <Text>• Expiry date:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse
              response={<FillField value={formatRabiesDuration(fields.duration)} />}
            >
              <Text>• Vaccine validity:</Text>
            </WithInlineResponse>
          </View>
          <View style={styles.listItem}>
            <WithInlineResponse
              response={<FillField value={formatCertDate(fields.nextDueDate) || undefined} />}
            >
              <Text>• Next vaccination due:</Text>
            </WithInlineResponse>
          </View>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            Please feel free to contact us for any further information or clarification. On behalf
            of Snoots Vet.
          </Text>
        </View>

        <VetSignoff
          vetName={fields.vetName}
          label="Dr"
          licenseNumber={fields.vetLicenseNumber || undefined}
          registrationLabel="License number"
          showSignature
          showDate
        />
      </Page>
    </Document>
  )
}
